import React from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function MenuItem({ name, description }) {
  return (
    <div className="py-2 border-b border-[#1F1F1F] last:border-0">
      <h4 className="font-heading text-base text-[#EDEDED]">{name}</h4>
      {description && <p className="font-body text-sm text-[#A1A1A1] mt-1">{description}</p>}
    </div>
  );
}

function MenuCategory({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-10">
      <h3 className="font-heading text-xl font-semibold text-[#C8A46A] mb-4 pb-2 border-b border-[#C8A46A]/30 uppercase tracking-wider">
        {title.replace(/_/g, ' ')}
      </h3>
      {items.map(function(item, idx) {
        return <MenuItem key={idx} name={item.name} description={item.description} />;
      })}
    </div>
  );
}

function Menu() {
  const [menu, setMenu] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState('cocktails');
  const { t, language } = useLanguage();

  const catTitle = React.useMemo(function() {
    if (language === 'en') {
      return {
        'Classics': 'Classics',
        'Cocktails': 'Cocktails',
        'Hip & Trendy': 'Hip & Trendy',
        'Creamy': 'Creamy',
        'Heavy': 'Heavy',
        'Mojito': 'Mojito',
        'Mocktails': 'Mocktails',
        'Signature Cocktails': 'Signature Cocktails',
        'Our Spritzes': 'Our Spritzes',
        'Homemade Lemonades': 'Homemade Lemonades',
        'Classic Apero': 'Classic Aperitif',
        'Prosecco': 'Prosecco',
        'Apero Tip!': 'Apero Tip!',
        'Champagne': 'Champagne',
        'White Wines': 'White Wines',
        'Tip!': 'Tip!',
        'Red Wines': 'Red Wines',
        'Rosé Wines': 'Rosé Wines',
        'Rum': 'Rum',
        'Whiskey': 'Whiskey',
        'Gin': 'Gin',
        'Local Gin': 'Local Gin',
        'Vodka': 'Vodka',
        'Bourbon': 'Bourbon',
        'Tequila': 'Tequila',
        'Calvados': 'Calvados',
        'Cognac': 'Cognac',
        'Liquor': 'Liquor',
        'Jenever': 'Jenever',
        'Van het Vat': 'On Tap',
        'Tip! Van het Vat': 'Tip!',
        'Op Fles': 'Bottled',
        'Lokaal': 'Local',
        'Alcoholvrij': 'Non-Alcoholic',
        'Soft Drinks': 'Soft Drinks',
        'Waters': 'Waters',
        'Koffie': 'Coffee',
        'Chai Latte': 'Chai Latte',
        'Hot Chocolat': 'Hot Chocolate',
        'Tea Time Tip': 'Tea Time Tip',
        'Tipsy Coffee': 'Tipsy Coffee',
        'Thee': 'Tea',
        'Infusions': 'Infusions',
        'Barfood': 'Barfood',
        'Our Classics': 'Our Classics',
        'Add Some Carbs': 'Add Some Carbs',
        'Streetfood': 'Streetfood',
        'Our Sweets': 'Our Sweets',
        'Herfstsuggesties Food': 'Autumn Suggestions Food',
        'Herfstsuggesties Drinks': 'Autumn Cocktails & Drinks',
        'Autumn Lunch': 'Autumn Lunch',
      };
    }
    return {};
  }, [language]);

  function ct(title) {
    return catTitle[title] || title;
  }

  React.useEffect(function() {
    axios.get(BACKEND_URL + '/api/menu')
      .then(function(response) {
        setMenu(response.data);
        setLoading(false);
      })
      .catch(function(error) {
        console.error('Error fetching menu:', error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#0A0A0A] pt-24 flex items-center justify-center">
        <div className="text-[#C8A46A] font-heading text-xl">{t.menuPage.loading}</div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0A0A0A] pt-24" data-testid="menu-page">
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-body text-sm tracking-[0.2em] text-[#C8A46A] mb-4 uppercase"
          >
            {t.menuPage.subtitle}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl md:text-7xl font-bold text-[#EDEDED] mb-6"
          >
            {t.menuPage.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-body text-[#A1A1A1] text-lg max-w-2xl mx-auto"
          >
            {t.menuPage.description}
          </motion.p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-4 md:px-12">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="overflow-x-auto pb-4 mb-8 -mx-4 px-4">
              <TabsList className="inline-flex gap-2 bg-transparent min-w-max">
                <TabsTrigger value="cocktails" className="font-heading text-xs md:text-sm tracking-wider uppercase px-3 md:px-4 py-2 md:py-3 data-[state=active]:bg-[#C8A46A] data-[state=active]:text-[#0A0A0A] data-[state=inactive]:bg-[#141414] data-[state=inactive]:text-[#A1A1A1] rounded-sm border border-[#2A2A2A] data-[state=active]:border-[#C8A46A] whitespace-nowrap" data-testid="menu-tab-cocktails">{t.menuPage.tabs.cocktails}</TabsTrigger>
                <TabsTrigger value="aperitief" className="font-heading text-xs md:text-sm tracking-wider uppercase px-3 md:px-4 py-2 md:py-3 data-[state=active]:bg-[#C8A46A] data-[state=active]:text-[#0A0A0A] data-[state=inactive]:bg-[#141414] data-[state=inactive]:text-[#A1A1A1] rounded-sm border border-[#2A2A2A] data-[state=active]:border-[#C8A46A] whitespace-nowrap" data-testid="menu-tab-aperitief">{t.menuPage.tabs.aperitief}</TabsTrigger>
                <TabsTrigger value="wijnen" className="font-heading text-xs md:text-sm tracking-wider uppercase px-3 md:px-4 py-2 md:py-3 data-[state=active]:bg-[#C8A46A] data-[state=active]:text-[#0A0A0A] data-[state=inactive]:bg-[#141414] data-[state=inactive]:text-[#A1A1A1] rounded-sm border border-[#2A2A2A] data-[state=active]:border-[#C8A46A] whitespace-nowrap" data-testid="menu-tab-wijnen">{t.menuPage.tabs.wines}</TabsTrigger>
                <TabsTrigger value="spirits" className="font-heading text-xs md:text-sm tracking-wider uppercase px-3 md:px-4 py-2 md:py-3 data-[state=active]:bg-[#C8A46A] data-[state=active]:text-[#0A0A0A] data-[state=inactive]:bg-[#141414] data-[state=inactive]:text-[#A1A1A1] rounded-sm border border-[#2A2A2A] data-[state=active]:border-[#C8A46A] whitespace-nowrap" data-testid="menu-tab-spirits">{t.menuPage.tabs.spirits}</TabsTrigger>
                <TabsTrigger value="bieren" className="font-heading text-xs md:text-sm tracking-wider uppercase px-3 md:px-4 py-2 md:py-3 data-[state=active]:bg-[#C8A46A] data-[state=active]:text-[#0A0A0A] data-[state=inactive]:bg-[#141414] data-[state=inactive]:text-[#A1A1A1] rounded-sm border border-[#2A2A2A] data-[state=active]:border-[#C8A46A] whitespace-nowrap" data-testid="menu-tab-bieren">{t.menuPage.tabs.beers}</TabsTrigger>
                <TabsTrigger value="dranken" className="font-heading text-xs md:text-sm tracking-wider uppercase px-3 md:px-4 py-2 md:py-3 data-[state=active]:bg-[#C8A46A] data-[state=active]:text-[#0A0A0A] data-[state=inactive]:bg-[#141414] data-[state=inactive]:text-[#A1A1A1] rounded-sm border border-[#2A2A2A] data-[state=active]:border-[#C8A46A] whitespace-nowrap" data-testid="menu-tab-dranken">{t.menuPage.tabs.drinks}</TabsTrigger>
                <TabsTrigger value="sharing" className="font-heading text-xs md:text-sm tracking-wider uppercase px-3 md:px-4 py-2 md:py-3 data-[state=active]:bg-[#C8A46A] data-[state=active]:text-[#0A0A0A] data-[state=inactive]:bg-[#141414] data-[state=inactive]:text-[#A1A1A1] rounded-sm border border-[#2A2A2A] data-[state=active]:border-[#C8A46A] whitespace-nowrap" data-testid="menu-tab-sharing">{t.menuPage.tabs.sharingDishes}</TabsTrigger>
                <TabsTrigger value="herfstsuggesties" className="font-heading text-xs md:text-sm tracking-wider uppercase px-3 md:px-4 py-2 md:py-3 data-[state=active]:bg-[#C8A46A] data-[state=active]:text-[#0A0A0A] data-[state=inactive]:bg-[#141414] data-[state=inactive]:text-[#A1A1A1] rounded-sm border border-[#2A2A2A] data-[state=active]:border-[#C8A46A] whitespace-nowrap" data-testid="menu-tab-herfstsuggesties">{language === 'en' ? 'Autumn Suggestions' : 'Herfstsuggesties'}</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="cocktails" className="mt-0">
              <div className="glass-card p-8 md:p-12">
                {menu && menu.cocktails && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div>
                      <MenuCategory title={ct("Classics")} items={menu.cocktails.classics} />
                    </div>
                    <div>
                      <MenuCategory title={ct("Hip & Trendy")} items={menu.cocktails.hip_trendy} />
                      <MenuCategory title={ct("Creamy")} items={menu.cocktails.creamy} />
                      <MenuCategory title={ct("Heavy")} items={menu.cocktails.heavy} />
                    </div>
                    <div>
                      <MenuCategory title={ct("Mojito")} items={menu.cocktails.mojito} />
                      <MenuCategory title={ct("Signature Cocktails")} items={menu.cocktails.signature} />
                      <MenuCategory title={ct("Mocktails")} items={menu.cocktails.mocktails} />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="aperitief" className="mt-0">
              <div className="glass-card p-8 md:p-12">
                {menu && menu.aperitief && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <MenuCategory title={ct("Our Spritzes")} items={menu.aperitief.spritzes} />
                      <MenuCategory title={ct("Homemade Lemonades")} items={menu.aperitief.lemonades} />
                    </div>
                    <div>
                      <MenuCategory title={ct("Classic Apero")} items={menu.aperitief.classic_apero} />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="wijnen" className="mt-0">
              <div className="glass-card p-8 md:p-12">
                {menu && menu.bubbels_wijnen && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <MenuCategory title={ct("Prosecco")} items={menu.bubbels_wijnen.prosecco} />
                      <MenuCategory title={ct("Apero Tip!")} items={menu.bubbels_wijnen.apero_tip} />
                      <MenuCategory title={ct("Champagne")} items={menu.bubbels_wijnen.champagne} />
                      <MenuCategory title={ct("Rosé Wines")} items={menu.bubbels_wijnen.rose_wines} />
                    </div>
                    <div>
                      <MenuCategory title={ct("White Wines")} items={menu.bubbels_wijnen.white_wines} />
                      <MenuCategory title={ct("Tip!")} items={menu.bubbels_wijnen.white_wines_tip} />
                      <MenuCategory title={ct("White Wines")} items={menu.bubbels_wijnen.white_wines_extra} />
                      <MenuCategory title={ct("Red Wines")} items={menu.bubbels_wijnen.red_wines} />
                      <MenuCategory title={ct("Tip!")} items={menu.bubbels_wijnen.red_wines_tip} />
                      <MenuCategory title={ct("Red Wines")} items={menu.bubbels_wijnen.red_wines_extra} />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="spirits" className="mt-0">
              <div className="glass-card p-8 md:p-12">
                {menu && menu.spirits && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div>
                      <MenuCategory title={ct("Rum")} items={menu.spirits.rum} />
                      <MenuCategory title={ct("Vodka")} items={menu.spirits.vodka} />
                      <MenuCategory title={ct("Bourbon")} items={menu.spirits.bourbon} />
                    </div>
                    <div>
                      <MenuCategory title={ct("Whiskey")} items={menu.spirits.whiskey} />
                      <MenuCategory title={ct("Tequila")} items={menu.spirits.tequila} />
                    </div>
                    <div>
                      <MenuCategory title={ct("Gin")} items={menu.spirits.gin} />
                      <MenuCategory title={ct("Local Gin")} items={menu.spirits.local_gin} />
                      <MenuCategory title={ct("Calvados")} items={menu.spirits.calvados} />
                      <MenuCategory title={ct("Cognac")} items={menu.spirits.cognac} />
                      <MenuCategory title={ct("Liquor")} items={menu.spirits.liquor} />
                      <MenuCategory title={ct("Jenever")} items={menu.spirits.jenever} />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="bieren" className="mt-0">
              <div className="glass-card p-8 md:p-12">
                {menu && menu.bieren && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div>
                      <MenuCategory title={ct("Van het Vat")} items={menu.bieren.van_het_vat} />
                      <MenuCategory title={ct("Tip! Van het Vat")} items={menu.bieren.vat_tip} />
                    </div>
                    <div>
                      <MenuCategory title={ct("Op Fles")} items={menu.bieren.fles} />
                    </div>
                    <div>
                      <MenuCategory title={ct("Lokaal")} items={menu.bieren.lokaal} />
                      <MenuCategory title={ct("Alcoholvrij")} items={menu.bieren.na_bieren} />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="dranken" className="mt-0">
              <div className="glass-card p-8 md:p-12">
                {menu && menu.dranken && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div>
                      <MenuCategory title={ct("Soft Drinks")} items={menu.dranken.soft_drinks} />
                      <MenuCategory title={ct("Waters")} items={menu.dranken.waters} />
                    </div>
                    <div>
                      <MenuCategory title={ct("Koffie")} items={menu.dranken.koffie} />
                      <MenuCategory title={ct("Chai Latte")} items={menu.dranken.chai_latte} />
                      <MenuCategory title={ct("Hot Chocolat")} items={menu.dranken.hot_chocolat} />
                      <MenuCategory title={ct("Tea Time Tip")} items={menu.dranken.tea_time_tip} />
                    </div>
                    <div>
                      <MenuCategory title={ct("Tipsy Coffee")} items={menu.dranken.tipsy_coffee} />
                      <MenuCategory title={ct("Thee")} items={menu.dranken.thee} />
                      <MenuCategory title={ct("Infusions")} items={menu.dranken.infusions} />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="sharing" className="mt-0">
              <div className="glass-card p-8 md:p-12">
                {menu && menu.sharing_dishes && (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                      <div>
                        <MenuCategory title={ct("Barfood")} items={menu.sharing_dishes.barfood} />
                        <MenuCategory title={ct("Add Some Carbs")} items={menu.sharing_dishes.add_some_carbs} />
                      </div>
                      <div>
                        <MenuCategory title={ct("Our Classics")} items={menu.sharing_dishes.classics} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <MenuCategory title={ct("Streetfood")} items={menu.sharing_dishes.streetfood} />
                      <MenuCategory title={ct("Our Sweets")} items={menu.sharing_dishes.sweets} />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="herfstsuggesties" className="mt-0">
              <div className="glass-card p-8 md:p-12">
                {menu && menu.herfstsuggesties && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <MenuCategory title={ct("Herfstsuggesties Food")} items={menu.herfstsuggesties.food} />
                    </div>
                    <div>
                      <MenuCategory title={ct("Herfstsuggesties Drinks")} items={menu.herfstsuggesties.drinks} />
                      <MenuCategory title={ct("Autumn Lunch")} items={menu.herfstsuggesties.lunch} />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

          </Tabs>
        </div>
      </section>
    </main>
  );
}

export default Menu;
